import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, Users, Boxes, Ban, ShieldCheck, Plus, Eye, EyeOff, KeyRound, Unlock, Shield, Copy, ImageOff, ShieldAlert, Palette } from 'lucide-react';
import AdminRolesTab from '@/pages/AdminRolesTab';
import AdminModerationTab from '@/pages/AdminModerationTab';
import AdminSettingsTab from '@/pages/AdminSettingsTab';
import AdminCategoriesTab from '@/pages/AdminCategoriesTab';
import AdminServiceTaxonomyTab from '@/pages/AdminServiceTaxonomyTab';
import SearchInput from '@/components/marketplace/SearchInput';
import UserAvatar from '@/components/marketplace/UserAvatar';

const AdminPanel = () => {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [userQ, setUserQ] = useState('');
  const [stats, setStats] = useState({});
  const [ltype, setLtype] = useState('sell');
  const [tree, setTree] = useState([]);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCat, setNewCat] = useState({ name: '', parent_id: '', listing_type: 'sell' });
  const [securityOpen, setSecurityOpen] = useState(false);
  const [securityData, setSecurityData] = useState(null);
  const [securityUser, setSecurityUser] = useState(null);
  const [resetIssued, setResetIssued] = useState(null); // {reset_url, expires_at, user_email}
  const [resetConfirmUser, setResetConfirmUser] = useState(null); // user object for confirmation dialog
  const [availableRoles, setAvailableRoles] = useState([
    { key: 'user', label: 'User' },
    { key: 'moderator', label: 'Moderator' },
    { key: 'admin', label: 'Admin' },
  ]);

  const loadRoles = async () => {
    try {
      const { data } = await api.get('/admin/rbac/roles');
      const active = (data.roles || []).filter(r => r.active !== false);
      if (active.length) setAvailableRoles(active.map(r => ({ key: r.key, label: r.label })));
    } catch {
      // Silently fall back to hardcoded 3-role list on failure
    }
  };

  const loadUsers = async (query) => {
    const q = query !== undefined ? query : userQ;
    const { data } = await api.get('/admin/users', { params: q ? { q } : {} });
    setUsers(data);
  };

  const loadTree = async () => {
    const { data } = await api.get('/categories/tree', { params: { listing_type: ltype, include_hidden: true } });
    setTree(data);
  };

  const loadStats = async () => {
    const { data } = await api.get('/admin/stats');
    setStats(data);
  };

  useEffect(() => { loadUsers(); loadStats(); loadRoles(); }, []);
  useEffect(() => { loadTree(); }, [ltype]);

  const updateUser = async (id, patch) => {
    try {
      const params = new URLSearchParams(patch).toString();
      await api.patch(`/admin/users/${id}?${params}`);
      toast.success('Updated');
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const banUser = async (u) => {
    // Confirm before banning to prevent accidental clicks
    if (!window.confirm(`Ban ${u.email}?\n\nThis will immediately log them out and block future logins. You can activate them again from this same panel.`)) {
      return;
    }
    return updateUser(u.id, { status: 'banned' });
  };

  const openSecurity = async (u) => {
    setSecurityUser(u);
    setSecurityOpen(true);
    setSecurityData(null);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/security`);
      setSecurityData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load security info');
    }
  };

  const doUnlock = async (userId) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/unlock`);
      if (data.was_locked) toast.success('Account unlocked');
      else toast.info('Account was not locked');
      // Refresh security view
      const { data: fresh } = await api.get(`/admin/users/${userId}/security`);
      setSecurityData(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const sendResetLink = async (u) => {
    // opens confirmation AlertDialog; actual issue happens in confirmSendResetLink
    setResetConfirmUser(u);
  };

  const confirmSendResetLink = async () => {
    const u = resetConfirmUser;
    if (!u) return;
    setResetConfirmUser(null);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/password-reset`);
      setResetIssued({ ...data, user_email: u.email });
      toast.success(`Reset link sent to ${u.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const copyResetLink = () => {
    if (!resetIssued?.reset_url) return;
    navigator.clipboard.writeText(resetIssued.reset_url);
    toast.success('Reset link copied');
  };

  const removeUserPhoto = async (u) => {
    if (!window.confirm(`Remove profile photo for ${u.email}?\n\nThis is used for content moderation — the user can upload a new one at any time.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/photo/remove`);
      toast.success('Profile photo removed');
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggleCategory = async (cat, field) => {
    try {
      await api.patch(`/categories/${cat.id}`, { [field]: !cat[field] });
      toast.success('Updated');
      loadTree();
    } catch { toast.error('Failed'); }
  };

  const createCategory = async () => {
    if (!newCat.name) return toast.error('Name required');
    try {
      await api.post('/categories', {
        name: newCat.name,
        listing_type: newCat.listing_type || ltype,
        parent_id: newCat.parent_id || null,
        display_order: 100,
        active: true,
        hidden: false,
      });
      toast.success('Category created');
      setNewCatOpen(false); setNewCat({ name: '', parent_id: '', listing_type: ltype });
      loadTree();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Admin Panel</h1>
        <p className='text-muted-foreground text-sm'>Manage users, moderators, categories, and platform-wide settings.</p>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        {[
          ['Total users', stats.total_users],
          ['Live listings', stats.live_listings],
          ['Total inquiries', stats.inquiries],
          ['Open reports', stats.open_reports],
        ].map(([k, v]) => (
          <Card key={k} className='p-4'>
            <div className='text-xs uppercase tracking-wider text-muted-foreground'>{k}</div>
            <div className='text-2xl font-semibold mt-1 tabular-nums'>{v ?? 0}</div>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid='admin-tabs'>
          <TabsTrigger value='users' data-testid='admin-tab-users'><Users className='h-4 w-4 mr-1' /> Users</TabsTrigger>
          <TabsTrigger value='categories' data-testid='admin-tab-categories'><Boxes className='h-4 w-4 mr-1' /> Categories</TabsTrigger>
          <TabsTrigger value='service-taxonomy' data-testid='admin-tab-service-taxonomy'><Boxes className='h-4 w-4 mr-1' /> Service Taxonomy</TabsTrigger>
          <TabsTrigger value='roles' data-testid='admin-tab-roles'><Shield className='h-4 w-4 mr-1' /> Roles &amp; Permissions</TabsTrigger>
          <TabsTrigger value='moderation' data-testid='admin-tab-moderation'><ShieldAlert className='h-4 w-4 mr-1' /> Moderation</TabsTrigger>
          <TabsTrigger value='settings' data-testid='admin-tab-settings'><Palette className='h-4 w-4 mr-1' /> Platform Settings</TabsTrigger>
        </TabsList>

        <TabsContent value='users' className='space-y-3'>
          <Card className='p-4'>
            <div className='flex gap-2 mb-3'>
              <SearchInput
                value={userQ}
                onChange={setUserQ}
                onDebouncedChange={(v) => loadUsers(v)}
                placeholder='Search users by name, email, or company (live)'
                testid='admin-users-search'
              />
            </div>
            <table className='w-full text-sm'>
              <thead className='text-left'>
                <tr className='border-b'>
                  <th className='py-2 pr-3'>Name</th>
                  <th className='py-2 pr-3'>Email</th>
                  <th className='py-2 pr-3'>Company</th>
                  <th className='py-2 pr-3'>Role</th>
                  <th className='py-2 pr-3'>Status</th>
                  <th className='py-2 pr-3'>Verified</th>
                  <th className='py-2 pr-3'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className='border-b last:border-b-0 hover:bg-secondary/50' data-testid='admin-users-row'>
                    <td className='py-2 pr-3 font-medium'>
                      <div className='flex items-center gap-2'>
                        <UserAvatar
                          name={u.name}
                          photo={u.profile_photo_thumb || u.profile_photo}
                          updatedAt={u.profile_photo_updated_at}
                          size={28}
                        />
                        <Link
                          to={`/admin/users/${u.id}`}
                          className='hover:underline text-primary'
                          data-testid={`admin-user-link-${u.id}`}
                          title='Open User 360°'
                        >{u.name || u.email}</Link>
                      </div>
                    </td>
                    <td className='py-2 pr-3 text-muted-foreground'>{u.email}</td>
                    <td className='py-2 pr-3 text-muted-foreground'>{u.company_name || '—'}</td>
                    <td className='py-2 pr-3'>
                      <Select value={u.role} onValueChange={(v) => updateUser(u.id, { role: v })}>
                        <SelectTrigger className='h-8 w-32'><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {availableRoles.map(r => (
                            <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className='py-2 pr-3'>
                      <span className={`inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium ${u.status === 'banned' ? 'status-rejected' : 'status-approved'}`}>{u.status}</span>
                    </td>
                    <td className='py-2 pr-3 text-xs'>{u.verification_level?.replace(/_/g, ' ')}</td>
                    <td className='py-2 pr-3'>
                      <div className='flex gap-1 flex-wrap'>
                        {u.status === 'banned' ? (
                          <Button size='sm' variant='outline' onClick={() => updateUser(u.id, { status: 'active' })} data-testid={`admin-unban-${u.id}`}><ShieldCheck className='h-3.5 w-3.5 mr-1' /> Activate</Button>
                        ) : (
                          <Button size='sm' variant='outline' onClick={() => banUser(u)} data-testid={`admin-ban-${u.id}`}><Ban className='h-3.5 w-3.5 mr-1' /> Ban</Button>
                        )}
                        {/* NOTE: The direct "Verify" action was removed intentionally
                            (Feb 2026). Company verification now runs exclusively
                            through the document-upload → moderator-review flow at
                            /verification (see VerificationCenter). Admins with a
                            legitimate override case should mark verification from
                            User 360 → GST card, which requires a prior submission. */}
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => openSecurity(u)}
                          data-testid={`admin-security-${u.id}`}
                          title='View security & login attempts'
                        >
                          <Shield className='h-3.5 w-3.5 mr-1' /> Security
                        </Button>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => sendResetLink(u)}
                          data-testid={`admin-send-reset-${u.id}`}
                          title='Send password reset link to user'
                        >
                          <KeyRound className='h-3.5 w-3.5 mr-1' /> Send reset
                        </Button>
                        {u.profile_photo && (
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => removeUserPhoto(u)}
                            data-testid={`admin-remove-photo-${u.id}`}
                            title='Force-remove profile photo (moderation)'
                            className='text-destructive hover:text-destructive'
                          >
                            <ImageOff className='h-3.5 w-3.5 mr-1' /> Photo
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value='categories' className='space-y-3'>
          <AdminCategoriesTab />
        </TabsContent>

        <TabsContent value='service-taxonomy' className='space-y-3'>
          <AdminServiceTaxonomyTab />
        </TabsContent>

        <TabsContent value='roles' className='space-y-3'>
          <AdminRolesTab />
        </TabsContent>

        <TabsContent value='moderation' className='space-y-3'>
          <AdminModerationTab />
        </TabsContent>

        <TabsContent value='settings' className='space-y-3'>
          <AdminSettingsTab />
        </TabsContent>
      </Tabs>

      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>New category</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div>
              <Label>Listing type</Label>
              <Select value={newCat.listing_type} onValueChange={(v) => setNewCat({ ...newCat, listing_type: v, parent_id: '' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value='sell'>Sell</SelectItem>
                  <SelectItem value='wanted'>Wanted</SelectItem>
                  <SelectItem value='rent'>Rent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parent (optional; leave empty to create top-level)</Label>
              <Select value={newCat.parent_id} onValueChange={(v) => setNewCat({ ...newCat, parent_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder='Top-level' /></SelectTrigger>
                <SelectContent>
                  <SelectItem value='__none__'>Top-level</SelectItem>
                  {tree.filter(t => t.listing_type === newCat.listing_type).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid='admin-new-cat-name' />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setNewCatOpen(false)}>Cancel</Button>
            <Button onClick={createCategory} data-testid='admin-new-cat-submit'>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security dialog (login attempts + unlock) */}
      <Dialog open={securityOpen} onOpenChange={setSecurityOpen}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>
              Security — {securityUser?.name || securityUser?.email}
            </DialogTitle>
          </DialogHeader>
          {!securityData ? (
            <div className='text-sm text-muted-foreground py-6'>Loading…</div>
          ) : (
            <div className='space-y-4' data-testid='admin-security-dialog'>
              <div className='grid grid-cols-2 gap-3'>
                <Card className='p-3'>
                  <div className='text-xs uppercase text-muted-foreground'>Lock status</div>
                  {securityData.locked ? (
                    <div className='mt-1 flex items-center gap-2 text-[hsl(var(--danger))] font-semibold'>
                      <Ban className='h-4 w-4' /> Locked
                      <Button size='sm' variant='outline' className='ml-auto' onClick={() => doUnlock(securityUser.id)} data-testid='admin-unlock-btn'>
                        <Unlock className='h-3.5 w-3.5 mr-1' /> Unlock now
                      </Button>
                    </div>
                  ) : (
                    <div className='mt-1 flex items-center gap-2 text-[hsl(var(--verified))] font-semibold'>
                      <ShieldCheck className='h-4 w-4' /> Active
                    </div>
                  )}
                  {securityData.lock_until && securityData.locked && (
                    <div className='text-xs text-muted-foreground mt-1'>
                      Unlocks at: {new Date(securityData.lock_until).toLocaleString('en-IN')}
                    </div>
                  )}
                </Card>
                <Card className='p-3'>
                  <div className='text-xs uppercase text-muted-foreground'>Last successful login</div>
                  <div className='mt-1 font-medium text-sm'>
                    {securityData.last_login_ts
                      ? new Date(securityData.last_login_ts).toLocaleString('en-IN')
                      : '—'}
                  </div>
                  {securityData.last_login_ip && (
                    <div className='text-xs text-muted-foreground mt-0.5 font-mono'>{securityData.last_login_ip}</div>
                  )}
                </Card>
              </div>

              <div>
                <div className='text-xs uppercase text-muted-foreground mb-2'>
                  Recent login attempts ({securityData.attempts?.length || 0})
                </div>
                <div className='max-h-80 overflow-y-auto border rounded-lg'>
                  <table className='w-full text-xs' data-testid='admin-security-attempts-table'>
                    <thead className='sticky top-0 bg-secondary/80 backdrop-blur'>
                      <tr>
                        <th className='py-1.5 px-2 text-left'>When</th>
                        <th className='py-1.5 px-2 text-left'>Result</th>
                        <th className='py-1.5 px-2 text-left'>IP</th>
                        <th className='py-1.5 px-2 text-left'>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(securityData.attempts || []).map((a, idx) => (
                        <tr key={idx} className='border-t'>
                          <td className='py-1.5 px-2 text-muted-foreground'>
                            {new Date(a.ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className='py-1.5 px-2'>
                            <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${a.success ? 'bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]' : 'bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]'}`}>
                              {a.success ? 'success' : 'fail'}
                            </span>
                          </td>
                          <td className='py-1.5 px-2 font-mono'>{a.ip}</td>
                          <td className='py-1.5 px-2 text-muted-foreground'>{a.reason || '—'}</td>
                        </tr>
                      ))}
                      {(!securityData.attempts || securityData.attempts.length === 0) && (
                        <tr><td colSpan={4} className='py-6 text-center text-muted-foreground'>No login attempts recorded yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setSecurityOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm send-reset AlertDialog */}
      <AlertDialog open={!!resetConfirmUser} onOpenChange={(v) => { if (!v) setResetConfirmUser(null); }}>
        <AlertDialogContent data-testid='admin-reset-confirm-dialog'>
          <AlertDialogHeader>
            <AlertDialogTitle>Send password reset link?</AlertDialogTitle>
            <AlertDialogDescription>
              A one-time reset link (valid 30 minutes) will be emailed to{' '}
              <strong>{resetConfirmUser?.email}</strong>. You can also copy the link afterwards to share it directly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid='admin-reset-cancel'>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSendResetLink} data-testid='admin-reset-confirm'>
              Send reset link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset link issued dialog */}
      <Dialog open={!!resetIssued} onOpenChange={(v) => { if (!v) setResetIssued(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Password reset link issued</DialogTitle></DialogHeader>
          {resetIssued && (
            <div className='space-y-3 text-sm' data-testid='admin-reset-issued-dialog'>
              <p>
                A password reset link has been emailed to <strong>{resetIssued.user_email}</strong>.
                If they cannot access their email, you can copy the link below and share it securely.
              </p>
              <div className='rounded-lg border bg-secondary/40 p-3 space-y-2'>
                <div className='text-[11px] uppercase tracking-wider text-muted-foreground'>Reset URL</div>
                <div className='font-mono text-xs break-all'>{resetIssued.reset_url}</div>
                <Button size='sm' variant='outline' onClick={copyResetLink} data-testid='admin-copy-reset-link'>
                  <Copy className='h-3.5 w-3.5 mr-1' /> Copy link
                </Button>
              </div>
              <div className='text-xs text-muted-foreground'>
                Expires at: {new Date(resetIssued.expires_at).toLocaleString('en-IN')} (30 minutes from now)
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetIssued(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
